import {
    useCallback,
    useEffect,
    useMemo,
    type ReactNode,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { createAppChatService } from '@/entities/AppChat/api/appChatService';
import { useAppChatStore } from '@/entities/AppChat/model/store';
import { createChatDialogStore } from '@/entities/ChatDialog/model/store';
import { ChatDialogWorkspaceContext } from '@/entities/ChatDialog/model/workspace';
import { connectChatDialogRealtime } from '@/entities/ChatDialog/api/chatDialogRealtime';
import { useAuth } from '@/entities/Auth/store';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { useStore } from 'zustand';
import { createAppChatRouteStore } from '@/entities/AppChat/model/routeStore';
import { AppChatActions } from './AppChatActions';
import { RequestError } from '@/shared/ui/RequestState';
import { ChatDialogWorkspace } from '@/features/ChatDialog/Workspace/ChatDialogWorkspace';
import { useAppChatRealtime } from '@/features/appChatRealtime/model/useAppChatRealtime';
import { ChatDialogRealtimeContext, useChatDialogRealtime } from '@/features/chatDialogRealtime/model/useChatDialogRealtime';
import type { ChatDialogRealtimeCallbacks } from '@/entities/ChatDialog/model/types';

interface AppChatWorkspaceProps {
    /** Владелец переписки для просмотра сотрудником */
    ownerAppUserId?: string;
    listContent?: ReactNode;

    /** Действия выбранной переписки */
    headerActions?: ReactNode;
}

/** Показывает переписку приложения в общем рабочем пространстве */
export const AppChatWorkspace = (
    { ownerAppUserId, headerActions, listContent }: AppChatWorkspaceProps
) => {
    const [searchParams, setSearchParams] = useSearchParams();
    const sessionRevision = useAuth((state) => state.sessionRevision);
    const canView = useCanAccessPermission('admin.chats.view');
    const canCreate = useCanAccessPermission('admin.chats.send') && !ownerAppUserId;
    const service = useMemo(() => createAppChatService(ownerAppUserId), [ownerAppUserId]);

    const store = useMemo(() => ownerAppUserId
        ? createChatDialogStore(service)
        : useAppChatStore, [ownerAppUserId, service]);

    const routeStore = useMemo(() => createAppChatRouteStore(service), [service]);
    const routeError = useStore(routeStore, (state) => state.error);

    const sharedRealtime = useAppChatRealtime();

    /** Подписывает выбранного владельца на обновления переписки */
    const connect = useCallback((callbacks: ChatDialogRealtimeCallbacks) => (
        connectChatDialogRealtime(callbacks, service)
    ), [service]);

    const ownRealtime = useChatDialogRealtime(canView && Boolean(ownerAppUserId), undefined, store, connect);

    const realtime = ownerAppUserId
        ? ownRealtime
        : sharedRealtime;

    const legacyId = searchParams.get('chat_id') ?? searchParams.get('chatId');

    const companionId = !ownerAppUserId
        ? searchParams.get('app_user_id')
        : null;

    const selectedId = searchParams.get('dialogId');

    useEffect(() => () => {
        if (ownerAppUserId) store.getState().reset();
    }, [ownerAppUserId, sessionRevision, store]);

    useEffect(() => {
        if (!canView || selectedId || (!legacyId && !companionId)) return;
        let current = true;


        void routeStore.getState()
            .resolve(legacyId, companionId, canCreate)
            .then((id) => {
                if (!current || !id) return;

                setSearchParams((previous) => {
                    const next = new URLSearchParams(previous);
                    next.delete('chat_id');
                    next.delete('chatId');
                    next.delete('app_user_id');
                    next.set('dialogId', id);

                    return next;
                }, { replace: true });
            });

        return () => {
            current = false;
            routeStore.getState().reset();
        };
    }, [canView, canCreate, companionId, legacyId, selectedId, routeStore, setSearchParams, sessionRevision]);

    return (
        <ChatDialogWorkspaceContext.Provider value={store}>
            <ChatDialogRealtimeContext.Provider value={realtime}>
                {routeError &&
                    <RequestError
                        message={routeError}
                        compact
                    />
                }
                <ChatDialogWorkspace
                    application
                    historyOwnerAppUserId={ownerAppUserId}
                    listContent={listContent}
                    readOnly={Boolean(ownerAppUserId)}
                    headerActions={headerActions ?? <AppChatActions readOnly={Boolean(ownerAppUserId)} />}
                />
            </ChatDialogRealtimeContext.Provider>
        </ChatDialogWorkspaceContext.Provider>
    );
};
